import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { TableHeader } from 'components/table/table-content';
import { Filters, filterTypes } from 'elements/Filter';
import { FilterManager } from 'lib/tables/FilterManager';
import { ServiceReportService } from 'services/service-report-service';
import { ServiceTaskTypeService } from 'services/service-task-type-service';
import { ErrorService } from 'services/error-service';
import { UserService } from 'services/user-service';
import { PubSub } from 'lib/event/PubSub';
import { Models } from 'models/core';
import { UserModels } from 'models/UserModels';
import { ServiceStationService } from 'services/service-station-service';

type ServiceHoursList = {
  Date: Date;
  NetId: string;
  ServiceId: number;
  ServiceStationName: string;
  Process: string;
  Hours: number;
  Comment: string;
  UserName: string;
};

@autoinject
export class ServiceHoursReport {
  protected serviceHoursListData: ServiceHoursList[] = [];

  protected serviceStations: Models.ServiceStation[] = [];
  protected serviceTaskTypes: Models.ServiceTaskType[] = [];
  protected users: UserModels.UserSimple[] = [];

  protected exportServiceHoursReportProgress = false;

  protected tableHeaders: TableHeader<ServiceHoursList>[] = [
    { key: 'Date', label: 'general.date', sortable: true },
    { key: 'NetId', label: 'general.NetId', sortable: true },
    { key: 'ServiceId', label: 'servicestation.serviceid', sortable: true },
    { key: 'ServiceStationName', label: 'general.servicestation', sortable: true },
    { key: 'Process', label: 'service.taskType', sortable: true },
    { key: 'Hours', label: 'general.numberOfHours', sortable: true },
    { key: 'Comment', label: 'general.comment', sortable: true },
    { key: 'UserName', label: 'general.user', sortable: true },
  ];

  constructor(
    private t: I18N,
    protected fm: FilterManager,
    protected userService: UserService,
    private errorService: ErrorService,
    protected serviceTaskTypeService: ServiceTaskTypeService,
    protected serviceReportService: ServiceReportService,
    protected serviceStationService: ServiceStationService,
    private pubsub: PubSub
  ) {
    pubsub.sub('filter-manager:filter-changed', () => {
      this.getServiceHoursReport();
    });
  }

  protected async attached() {
    await this.load();

    this.fm.init({
      context: 'service-hours-report',
      storageKey: 'service-hours-report',
      filters: {
        date: {
          name: 'date',
          label: this.t.tr('general.date'),
          type: filterTypes.RANGE_DATE,
        },
        serviceStationIds: {
          name: 'serviceStationIds',
          label: this.t.tr('general.servicestation'),
          type: filterTypes.CHECKBOX,
          options: this.serviceStations,
          query: 'serviceStationIds',
        },
        processIds: {
          name: 'processIds',
          label: this.t.tr('service.taskType'),
          type: filterTypes.CHECKBOX,
          options: this.serviceTaskTypes,
          query: 'processIds',
        },
        userIds: {
          name: 'userIds',
          label: this.t.tr('general.user'),
          type: filterTypes.CHECKBOX,
          options: this.users,
          query: 'userIds',
        },
      } satisfies Filters,
      defaultFilters: {
        date: true,
        serviceStationIds: true,
        processIds: true,
        userIds: true,
      },
    });
  }

  private async load() {
    const servicestations = await this.serviceStationService.getAll();
    const { data: users } = await this.userService.getServiceUsers();
    const serviceTaskTypes = await this.serviceTaskTypeService.getAll();

    this.serviceStations = servicestations;
    this.users = users;
    this.serviceTaskTypes = serviceTaskTypes;
  }

  protected async getServiceHoursReport() {
    try {
      const response = await this.serviceReportService.getServiceHoursReport(this.fm.queryFilter);
      if (!response) return;
      this.fm.paginationTotalRecords = +response.headers.get('x-total-count') || 0;

      this.serviceHoursListData = response.data;
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  protected async exportServiceHoursReport() {
    this.exportServiceHoursReportProgress = true;
    try {
      await this.serviceReportService.getServiceHoursReportExport(this.fm.queryFilter);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.exportServiceHoursReportProgress = false;
    }
  }

  protected setOrderByColumn(column: string) {
    this.fm.setOrderByColumn(column);
  }

  protected detached() {
    this.pubsub.unsub();
  }

  protected unbind() {
    this.pubsub.unsub();
  }
}
